import { Button, IconButton } from '@ads/components-react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import * as Dialog from '@radix-ui/react-dialog';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export interface ResetPasswordModalProps {
  /** Nome da pessoa cuja senha será resetada */
  readonly personName: string;
  /** Controla a visibilidade do modal */
  readonly open: boolean;
  /** Callback para fechar o modal */
  readonly onClose: () => void;
  /** Callback executado ao confirmar o reset */
  readonly onConfirm: () => void;
  /** Desabilita o botão de confirmação durante a requisição */
  readonly isLoading?: boolean;
}

/**
 * Modal de confirmação para resetar a senha de um usuário para a senha padrão da empresa.
 *
 * @param personName - Nome da pessoa cuja senha será resetada
 * @param open - Controla a visibilidade do modal
 * @param onClose - Callback para fechar o modal
 * @param onConfirm - Callback executado ao confirmar o reset
 * @param isLoading - Desabilita o botão de confirmação durante a requisição
 *
 * @example
 * ```tsx
 * <ResetPasswordModal
 *   personName="João Silva"
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onConfirm={handleResetPassword}
 *   isLoading={isPending}
 * />
 * ```
 */
export function ResetPasswordModal({
  personName,
  open,
  onClose,
  onConfirm,
  isLoading = false,
}: ResetPasswordModalProps) {
  const { t } = useTranslation('personManagement');

  useEffect(() => {
    if (open) return;
    const id = setTimeout(() => {
      document.body.style.removeProperty('pointer-events');
    }, 0);
    return () => clearTimeout(id);
  }, [open]);

  return (
    <Dialog.Root open={open} onOpenChange={o => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-none z-50 h-screen w-screen bg-base-gray-900 bg-opacity-medium backdrop-blur-soft ![animation:none]" />

        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 flex max-h-[85vh] w-full max-w-md -translate-x-1/2 -translate-y-1/2 flex-col rounded-md bg-ctx-layout-surface shadow-level3 focus:outline-none ![animation:none]"
          aria-describedby={undefined}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b-hairline border-ctx-layout-border px-xs py-mini">
            <Dialog.Title className="ts-heading-sm font-semibold text-ctx-content-title">
              {t('enrollmentManagement.resetPasswordModal.title')}
            </Dialog.Title>
            <Dialog.Close asChild>
              <IconButton
                icon={XMarkIcon}
                hierarchy="tertiary"
                size="sm"
                ariaLabel={t('actions.close')}
              />
            </Dialog.Close>
          </div>

          {/* Body */}
          <div className="px-xs py-mini">
            <p className="ts-paragraph-xs text-ctx-content-base">
              {t('enrollmentManagement.resetPasswordModal.message', {
                name: personName,
              })}
            </p>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-xs border-t-hairline border-ctx-layout-border px-md py-xs">
            <Button hierarchy="secondary" size="lg" onClick={onClose}>
              {t('enrollmentManagement.resetPasswordModal.cancel')}
            </Button>
            <Button
              hierarchy="primary"
              size="lg"
              isLoading={isLoading}
              disabled={isLoading}
              onClick={onConfirm}
            >
              {t('enrollmentManagement.resetPasswordModal.confirm')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
